import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { mapsApi } from '@/api/maps'

interface Props {
  lat: string  // form state values, may be empty / invalid
  lng: string
  onAddress: (formattedAddress: string) => void
  className?: string
  buttonLabel?: string
}

export function LookupAddressButton({ lat, lng, onAddress, className, buttonLabel }: Props) {
  const { t } = useTranslation('cameras')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const parsedLat = parseFloat(lat)
  const parsedLng = parseFloat(lng)
  const validCoords =
    Number.isFinite(parsedLat) && Number.isFinite(parsedLng) &&
    parsedLat >= -90 && parsedLat <= 90 &&
    parsedLng >= -180 && parsedLng <= 180

  async function handleClick() {
    if (!validCoords) {
      setError(t('edit.lookupAddressErrors.invalidCoords'))
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await mapsApi.reverseGeocode(parsedLat, parsedLng)
      if (result.formatted_address) {
        onAddress(result.formatted_address)
      } else {
        setError(t('edit.lookupAddressErrors.noAddress'))
      }
    } catch (e: any) {
      const status = e?.response?.status
      if (status === 429) {
        setError(t('edit.lookupAddressErrors.throttled'))
      } else if (status === 502 || status === 503) {
        setError(t('edit.lookupAddressErrors.unavailable'))
      } else if (status === 400) {
        setError(t('edit.lookupAddressErrors.badRequest'))
      } else {
        setError(t('edit.lookupAddressErrors.generic'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={loading || !validCoords}
        className="gap-1.5"
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <MapPin className="h-3.5 w-3.5" />
        )}
        {buttonLabel ?? t('edit.lookupAddress')}
      </Button>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  )
}
